/**
 * Main login page with email discovery and multi-tenant Okta support
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authApi, DomainDiscoveryResponse } from '../../services/authApi';
import './LoginPage.css';

const LoginPage: React.FC = () => {
  const { login, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [domainInfo, setDomainInfo] = useState<DomainDiscoveryResponse | null>(null);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showProviderSelection, setShowProviderSelection] = useState(false);

  // Check for error in URL params (from failed OAuth callback)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsDiscovering(true);
    setError(null);

    try {
      const discoveryResult = await authApi.discoverDomain(email);
      setDomainInfo(discoveryResult);
      setShowProviderSelection(true); // Always show provider selection
    } catch (err: any) {
      console.error('Domain discovery failed:', err);
      setError(err.response?.data?.detail || 'Failed to discover authentication options for this email domain');
      setShowProviderSelection(true); // Still allow login options
    } finally {
      setIsDiscovering(false);
    }
  };

  const handleGoogleLogin = async () => {
    console.log('🔍 handleGoogleLogin called');
    try {
      setError(null);
      console.log('🔍 Calling login function...');
      // For Google login, we can pass an empty email or placeholder since Google will handle user identification
      await login('', 'google');
      console.log('✅ Login function completed');
    } catch (err: any) {
      console.error('❌ Google login failed:', err);
      setError(err.message || 'Failed to start Google authentication');
    }
  };

  const handleMicrosoftLogin = async () => {
    console.log('🔍 handleMicrosoftLogin called');
    try {
      setError(null);
      console.log('🔍 Calling login function...');
      // For Microsoft login, we can pass an empty email or placeholder since Microsoft will handle user identification
      await login('', 'microsoft');
      console.log('✅ Login function completed');
    } catch (err: any) {
      console.error('❌ Microsoft login failed:', err);
      setError(err.message || 'Failed to start Microsoft authentication');
    }
  };

  const handleProviderLogin = async (provider: 'google' | 'okta') => {
    try {
      setError(null);
      await login(email, provider);
      // login() will redirect to OAuth provider
    } catch (err: any) {
      console.error(`${provider} login failed:`, err);
      setError(err.message || `Failed to start ${provider} authentication`);
    }
  };

  const handleRequestAccess = () => {
    // For now, show instructions to contact admin
    // In a full implementation, this could open a modal or redirect to a request form
    alert(`To enable ${domainInfo?.email_domain} authentication, please contact your system administrator.`);
  };

  const resetForm = () => {
    setEmail('');
    setDomainInfo(null);
    setShowProviderSelection(false);
    setError(null);
  };

  if (isLoading) {
    return (
      <div className="login-container">
        <div className="login-form">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Initializing...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-form">
        {/* Logo */}
        <div className="login-logo">
          <img 
            src="/logo192.png" 
            alt="Assertion" 
            className="logo-image"
            onError={(e) => {
              // Fallback if logo doesn't exist
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <h1>Assertion</h1>
        </div>

        {/* Organization branding */}
        {domainInfo?.custom_settings?.logo_url && (
          <div className="organization-branding">
            <img 
              src={domainInfo.custom_settings.logo_url} 
              alt={domainInfo.organization_name}
              className="organization-logo"
            />
          </div>
        )}

        {!showProviderSelection ? (
          // Email discovery form
          <form onSubmit={handleEmailSubmit} className="email-form">
            <h2>Sign in to your account</h2>
            <p className="subtitle">Enter your email to discover your organization's login options, or sign in with Google directly below</p>
            
            <div className="input-group">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                disabled={isDiscovering}
                required
                autoFocus
                className="email-input"
              />
              <button 
                type="submit"
                disabled={isDiscovering || !email}
                className="continue-button"
                style={{
                  backgroundColor: domainInfo?.custom_settings?.brand_colors?.primary || '#0066cc'
                }}
              >
                {isDiscovering ? (
                  <>
                    <span className="button-spinner"></span>
                    Discovering...
                  </>
                ) : (
                  'Continue'
                )}
              </button>
            </div>

            {/* Direct Google and Microsoft login options */}
            <div className="quick-login-options">
              <div className="divider">
                <span>or</span>
              </div>
              
              <button
                type="button"
                onClick={async () => {
                  console.log('🔍 Quick Google login button clicked!');
                  // Google login doesn't need email - Google will handle user identification
                  await handleGoogleLogin();
                }}
                className="provider-button google-button secondary"
              >
                <span className="provider-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </span>
                Sign in with Google
              </button>
              
              <button
                type="button"
                onClick={async () => {
                  console.log('🔍 Quick Microsoft login button clicked!');
                  // Microsoft login doesn't need email - Microsoft will handle user identification
                  await handleMicrosoftLogin();
                }}
                className="provider-button microsoft-button secondary"
              >
                <span className="provider-icon">
                  <svg width="18" height="18" viewBox="0 0 21 21">
                    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                  </svg>
                </span>
                Sign in with Microsoft
              </button>
            </div>
          </form>
        ) : (
          // Provider selection
          <div className="provider-selection">
            <div className="email-display">
              <span className="email-label">Signing in as:</span>
              <span className="email-value">{email}</span>
              <button onClick={resetForm} className="change-email-btn">
                Change
              </button>
            </div>

            <h3>Choose how to sign in</h3>

            {/* Google Login - Always available */}
            <button
              onClick={() => {
                console.log('🔍 Provider selection Google login button clicked!');
                handleGoogleLogin();
              }}
              className="provider-button google-button primary"
            >
              <span className="provider-icon">
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </span>
              Continue with Google
            </button>
            
            {/* Microsoft Login - Always available */}
            <button
              onClick={() => {
                console.log('🔍 Provider selection Microsoft login button clicked!');
                handleMicrosoftLogin();
              }}
              className="provider-button microsoft-button primary"
            >
              <span className="provider-icon">
                <svg width="18" height="18" viewBox="0 0 21 21">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
              </span>
              Continue with Microsoft
            </button>

            {domainInfo?.has_okta_config && (
              <>
                {/* Divider */}
                <div className="divider">
                  <span>or</span>
                </div>

                {/* Organization SSO */}
                <div className="organization-signin">
                  <h4>Sign in with your organization</h4>
                  <p className="organization-domain">{domainInfo.organization_name} ({domainInfo.okta_domain})</p>
                  
                  <button
                    onClick={() => handleProviderLogin('okta')}
                    className="provider-button okta-button"
                    style={{
                      backgroundColor: domainInfo.custom_settings?.brand_colors?.primary || '#0066cc'
                    }}
                  >
                    <span className="provider-icon">🏢</span>
                    Continue with {domainInfo.organization_name}
                  </button>
                </div>
              </>
            )}

            {!domainInfo?.has_okta_config && domainInfo && (
              <div className="no-sso-section">
                <div className="divider">
                  <span>Need SSO?</span>
                </div>
                <button
                  onClick={handleRequestAccess}
                  className="request-access-button"
                >
                  Request SSO for {domainInfo.email_domain}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}


        {/* Help links */}
        <div className="login-help">
          <p>
            <a href="/support" className="help-link">Need help?</a>
            {' • '}
            <a href="/privacy" className="help-link">Privacy Policy</a>
            {' • '}
            <a href="/terms" className="help-link">Terms of Service</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
